/*
  Sibling colour groups, read from the Linked Options app.

  The app stores each product's group in a metafield we can read with its own
  storefront token, so we get the colour name, image and stock state directly
  instead of guessing them from the product title.

  window.SSLinkedOptions.siblingsFor(productId) -> Promise<[{ id, handle, name, image, available, hex }]>
  Requests made in the same tick are batched into one GraphQL call.
*/
window.SSLinkedOptions = (function () {
  const CACHE_KEY = 'ss-lo-siblings-v1';
  const CACHE_TTL = 30 * 60 * 1000;
  const BATCH_SIZE = 30;
  const API_VERSION = '2026-07';

  /* only used for the small colour dot beside the dropdown */
  const COLOUR_MAP = [
    ['midnight black', '#2b2b2b'], ['ash grey', '#9a9da1'], ['baby blue', '#bfdcf7'], ['light pink', '#f9d6de'],
    ['light grey', '#d5d7da'], ['dark grey', '#5a5d61'], ['light brown', '#b8895c'], ['light green', '#c9e8c5'],
    ['rose pink', '#e8a0b0'], ['pastel pink', '#f7c6ce'], ['pastel blue', '#bfdcf7'], ['pastel green', '#c7ead9'],
    ['white lace', '#f5f2ee'], ['bubblegum', '#f7a6c1'], ['lavender', '#c9b3e8'], ['lavendar', '#c9b3e8'],
    ['blush', '#f4c6c6'], ['pink', '#f7c6ce'], ['black', '#2b2b2b'], ['white', '#f5f5f5'], ['grey', '#b6b8bb'],
    ['gray', '#b6b8bb'], ['sand', '#e3cba8'], ['tan', '#e3cba8'], ['beige', '#e8d9c2'], ['cream', '#efe3d2'],
    ['chocolate', '#6a4530'], ['brown', '#8b5a3c'], ['khaki', '#b7a77a'], ['mint', '#b9e7d0'], ['lemon', '#f7e88a'],
    ['yellow', '#f5d76e'], ['navy', '#2d3a5e'], ['blue', '#8fb8e8'], ['green', '#9fd39f'], ['orange', '#f7b26a'],
    ['red', '#e06b6b'], ['purple', '#b39ddb'], ['aqua', '#9be3e3'], ['gold', '#ffd700']
  ];

  function hexFor(name) {
    const t = String(name || '').toLowerCase();
    let best = null;
    for (const [n, hex] of COLOUR_MAP) {
      if (t.includes(n) && (!best || n.length > best[0].length)) best = [n, hex];
    }
    return best ? best[1] : '';
  }

  let cache = {};
  try {
    const c = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null');
    if (c && Date.now() - c.t < CACHE_TTL) cache = c.data || {};
  } catch (e) { /* ignore */ }

  function persist() {
    try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ t: Date.now(), data: cache })); } catch (e) { /* ignore */ }
  }

  function config() {
    const c = window.kingLinkedOptionConfig;
    if (c && c.storefrontAccessToken) {
      return { domain: c.shop || (window.Shopify && Shopify.shop), token: c.storefrontAccessToken };
    }
    return null;
  }

  async function query(cfg, gql) {
    const res = await fetch('https://' + cfg.domain + '/api/' + API_VERSION + '/graphql.json', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': cfg.token },
      body: JSON.stringify({ query: gql })
    });
    return res.json();
  }

  function groupFragment(alias, id) {
    return alias + ': product(id: "gid://shopify/Product/' + id + '") { id handle title ' +
      'group: metafield(namespace: "$app:king-linked-options", key: "group") { reference { ... on Metaobject { ' +
      'value: field(key: "value") { value } active: field(key: "active") { value } } } } }';
  }

  function parseGroup(node) {
    if (!node || !node.group || !node.group.reference) return null;
    const ref = node.group.reference;
    if (ref.active && String(ref.active.value) === 'false') return null;
    let parsed;
    try { parsed = JSON.parse(ref.value.value); } catch (e) { return null; }
    if (!parsed || !Array.isArray(parsed.products)) return null;
    return parsed.products
      .filter((x) => x && x.handle && x.published !== false && x.status !== 0)
      .map((x) => ({
        id: String(x.id),
        handle: x.handle,
        name: x.value || '',
        image: x.image || x.first_image || '',
        available: !x.out_of_stock,
        hex: x.color && /^#/.test(x.color) ? x.color : hexFor(x.value)
      }));
  }

  const pending = new Map(); // productId -> [resolve, ...]
  let timer = null;

  async function flush() {
    timer = null;
    const ids = Array.from(pending.keys());
    const waiting = new Map(pending);
    pending.clear();

    const cfg = config();
    const finish = () => waiting.forEach((resolvers, id) => resolvers.forEach((r) => r(cache[id] || [])));
    if (!cfg) { ids.forEach((id) => { cache[id] = cache[id] || []; }); finish(); return; }

    for (let i = 0; i < ids.length; i += BATCH_SIZE) {
      const chunk = ids.slice(i, i + BATCH_SIZE);
      try {
        const res = await query(cfg, '{' + chunk.map((id, k) => groupFragment('p' + k, id)).join('\n') + '}');
        chunk.forEach((id, k) => {
          const members = parseGroup(res.data && res.data['p' + k]);
          cache[id] = members || [];
          // a group is shared, so cache it for every member too
          if (members) members.forEach((m) => { cache[m.id] = members; });
        });
      } catch (e) {
        console.error('linked options:', e);
        chunk.forEach((id) => { cache[id] = cache[id] || []; });
      }
    }
    persist();
    finish();
  }

  function siblingsFor(productId) {
    const id = String(productId || '');
    if (!id) return Promise.resolve([]);
    if (cache[id]) return Promise.resolve(cache[id]);
    return new Promise((resolve) => {
      if (!pending.has(id)) pending.set(id, []);
      pending.get(id).push(resolve);
      if (!timer) timer = setTimeout(flush, 30);
    });
  }

  /* map of { productId: members } for a list of ids, in one batch */
  function siblingsForMany(ids) {
    return Promise.all(ids.map((id) => siblingsFor(id).then((m) => [String(id), m])))
      .then((pairs) => pairs.reduce((acc, [id, m]) => { acc[id] = m; return acc; }, {}));
  }

  return { siblingsFor, siblingsForMany, hexFor };
})();
